'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ClubOnlyPurchaseActions } from '@/ui/components/club-only-purchase-actions/club-only-purchase-actions';
import { useClubOnlyPurchase } from '@/ui/components/club-only-purchase-actions/use-club-only-purchase';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { Bundle } from '@/ui/services/BundleService';
import { CLUB_PDP_NAG_VALUE } from '@/utils/constants';
import {
  ClassicProductDetailTile,
  mapClassicPdpBreadcrumbs,
  mapClassicPdpUpsell,
} from '../classic-pdp-content';
import { ClassicPdpBundleData } from '../classic-pdp-entry';
import { ClassicPdpLayout } from '../classic-pdp-layout';
import { mapClassicPdpTileContent } from '../classic-pdp-tile-content';
import { ProductImageGallery } from '../product-image-gallery/product-image-gallery';
import { SmartHomeCompatibility } from '../smart-home-compatibility/smart-home-compatibility';
import { BundlePurchasePanel } from './bundle-purchase-panel';
import { useClassicBundleSelection } from './use-classic-bundle-selection';

export interface ClassicBundleDetailSectionProps {
  data: ClassicPdpBundleData;
  tile: ClassicProductDetailTile;
}

/**
 * The classic product detail layout for a bundle: the same grid and columns
 * as the product section, with the gallery following the selected variants
 * and a purchase panel that configures every product of the set.
 */
export function ClassicBundleDetailSection({
  data,
  tile,
}: Readonly<ClassicBundleDetailSectionProps>) {
  const t = useTranslations('ClassicProductDetail');
  const { country, areaVariables } = useLocalization();
  const priceVariantId = areaVariables?.priceVariantId;
  const productRuleService = useProductRuleService();

  const bundle = useMemo(() => new Bundle(data, priceVariantId), [data, priceVariantId]);
  const tileContent = useMemo(() => mapClassicPdpTileContent(tile), [tile]);
  const upsell = useMemo(() => mapClassicPdpUpsell(tile, priceVariantId), [tile, priceVariantId]);
  const homeLabel = t('home');
  const breadcrumbs = useMemo(
    () => mapClassicPdpBreadcrumbs(tile, null, bundle.name, homeLabel),
    [tile, bundle.name, homeLabel],
  );
  const selection = useClassicBundleSelection(bundle);
  const { selected, selectionKey } = selection;
  const images = useMemo(() => bundle.getImagesForSelection(selected), [bundle, selected]);
  const bundleConfiguration = bundle.getConfiguration(selected);
  const clubOnlyPurchase = useClubOnlyPurchase(
    productRuleService.isBundleRestrictedToNukiClub(bundleConfiguration),
  );

  const visuals = (
    <>
      <ProductImageGallery
        images={images}
        title={bundle.name}
        resetKey={selectionKey}
        badges={productRuleService.getBundleBadges(bundleConfiguration)}
      />
      <SmartHomeCompatibility icons={tileContent.compatibilityIcons} />
    </>
  );

  const actions = (
    <BundlePurchasePanel
      bundle={bundle}
      tileContent={tileContent}
      isVatNoteHidden={country.toUpperCase() === 'US'}
      selection={selection}
      isAddToCartDisabled={clubOnlyPurchase.gate === 'waiting'}
      purchaseActions={
        clubOnlyPurchase.gate === 'club-actions' ? (
          <ClubOnlyPurchaseActions
            isLoggedIn={clubOnlyPurchase.isLoggedIn}
            nag={CLUB_PDP_NAG_VALUE}
            onLogIn={clubOnlyPurchase.logInToUnlock}
          />
        ) : undefined
      }
    />
  );

  return (
    <ClassicPdpLayout
      breadcrumbs={breadcrumbs}
      visuals={visuals}
      actions={actions}
      upsell={upsell}
    />
  );
}
